// FILE: app/leaderboard/LeaderboardClient.tsx
"use client";

import { useState, useEffect, useTransition } from "react";
import { Crown, Search, Target, BookOpen, FileText, Brain, Clock, Trophy } from "lucide-react";
import { getLeaderboard, getMyRank, type LeaderboardEntry, type TimeRange, type SortBy } from "@/actions/leaderboard";

const TIME_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: "all", label: "Tất cả" },
  { value: "week", label: "Tuần này" },
  { value: "today", label: "Hôm nay" },
];

const SORT_OPTIONS: { value: SortBy; label: string; icon: any }[] = [
  { value: "xp",       label: "XP",        icon: Trophy },
  { value: "correct",  label: "Câu đúng",  icon: Target },
  { value: "accuracy", label: "Tỷ lệ",     icon: Target },
  { value: "paths",    label: "Lộ trình",  icon: BookOpen },
  { value: "exams",    label: "Đề thi",    icon: FileText },
  { value: "mindmaps", label: "Mindmap",   icon: Brain },
  { value: "time",     label: "Thời gian", icon: Clock },
];

function fmtTime(sec: number) {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}p`;
  return `${Math.floor(sec / 3600)}h`;
}

function Avatar({ url, name, size = 40 }: { url?: string | null; name?: string | null; size?: number }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  if (url) {
    return (
      <img
        src={url}
        alt={name || ""}
        style={{ width: size, height: size }}
        className="rounded-full object-cover border border-[#e2e8f0]"
      />
    );
  }
  return (
    <div
      style={{ width: size, height: size, fontSize: size * 0.38 }}
      className="rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-white font-bold"
    >
      {initial}
    </div>
  );
}

export default function LeaderboardClient() {
  const [timeRange, setTimeRange] = useState<TimeRange>("all");
  const [sortBy, setSortBy] = useState<SortBy>("xp");
  const [search, setSearch] = useState("");
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [myRank, setMyRank] = useState<LeaderboardEntry | null>(null);
  const [loading, startTransition] = useTransition();

  useEffect(() => {
    startTransition(async () => {
      const [lb, mine] = await Promise.all([
        getLeaderboard({ timeRange, sortBy, search, limit: 50 }),
        getMyRank({ timeRange, sortBy }),
      ]);
      setEntries(lb.entries);
      setMyRank(mine);
    });
  }, [timeRange, sortBy, search]);

  const top3 = entries.slice(0, 3);
  const rest = entries.slice(3);
  const myInTop = myRank && entries.some(e => e.user_id === myRank.user_id);

  return (
    <div className="space-y-5 pb-28">
      {/* ── HEADER CONTROLS ── */}
      <div className="bg-white rounded-2xl border border-[#e2e8f0] p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="inline-flex items-center bg-[#f1f5f9] rounded-xl p-1">
            {TIME_OPTIONS.map(t => (
              <button
                key={t.value}
                onClick={() => setTimeRange(t.value)}
                className={`px-4 py-1.5 rounded-lg text-[12px] font-bold transition-all ${
                  timeRange === t.value
                    ? "bg-white text-teal-700 shadow-sm"
                    : "text-[#64748b] hover:text-[#334155]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="relative flex-1 sm:max-w-xs ml-auto">
            <Search className="w-4 h-4 text-[#94a3b8] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Tìm người chơi..."
              className="w-full h-9 pl-9 pr-3 rounded-xl border border-[#e2e8f0] bg-[#fafbfc] text-[12px] outline-none focus:border-teal-500 focus:bg-white transition-all"
            />
          </div>
        </div>

        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-thin pb-1">
          {SORT_OPTIONS.map(s => {
            const Icon = s.icon;
            const active = sortBy === s.value;
            return (
              <button
                key={s.value}
                onClick={() => setSortBy(s.value)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold whitespace-nowrap transition-all ${
                  active
                    ? "bg-teal-800 text-white shadow-sm"
                    : "bg-[#f1f5f9] text-[#64748b] hover:bg-[#e2e8f0]"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── TOP 3 PODIUM ── */}
      {top3.length > 0 && (
        <div className="grid grid-cols-3 gap-3 sm:gap-4 items-end">
          <PodiumCard entry={top3[1]} rank={2} />
          <PodiumCard entry={top3[0]} rank={1} />
          <PodiumCard entry={top3[2]} rank={3} />
        </div>
      )}

      {/* ── LIST HẠNG 4+ ── */}
      {rest.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#e2e8f0] overflow-hidden">
          <div className="divide-y divide-[#f1f5f9]">
            {rest.map(e => (
              <LeaderboardRow key={e.user_id} entry={e} isMe={myRank?.user_id === e.user_id} />
            ))}
          </div>
        </div>
      )}

      {loading && entries.length === 0 && (
        <div className="h-60 rounded-2xl bg-white border border-[#e2e8f0] animate-pulse" />
      )}

      {!loading && entries.length === 0 && (
        <div className="bg-white rounded-2xl border border-[#e2e8f0] p-12 text-center">
          <Trophy className="w-10 h-10 text-[#cbd5e1] mx-auto mb-3" />
          <p className="text-[13px] font-bold text-[#334155]">Chưa có ai xếp hạng</p>
          <p className="text-[11px] text-[#94a3b8] mt-1">Hãy là người đầu tiên làm bài và leo top!</p>
        </div>
      )}

      {/* ── STICKY "BẠN" ── */}
      {myRank && !myInTop && (
        <div className="fixed bottom-4 left-4 right-4 max-w-5xl mx-auto z-40">
          <div className="bg-white rounded-2xl border-2 border-teal-500 shadow-[0_12px_32px_rgba(13,148,136,.2)] p-3">
            <LeaderboardRow entry={myRank} isMe compact />
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */

type PodiumConfig = {
  glow: string;
  bg: string;
  border: string;
  badge: string;
  text: string;
  emoji: string;
  scale: string;
  size: number;
};

const PODIUM_CONFIG: Record<number, PodiumConfig> = {
  1: {
    glow:   "ring-2 ring-yellow-400 shadow-[0_12px_32px_rgba(251,191,36,.35)]",
    bg:     "bg-gradient-to-br from-yellow-50 to-amber-100",
    border: "border-yellow-300",
    badge:  "from-yellow-400 to-amber-500",
    text:   "from-amber-600 to-orange-600",
    emoji:  "🥇",
    scale:  "scale-105 sm:scale-110",
    size:   64,
  },
  2: {
    glow:   "ring-1 ring-slate-300 shadow-[0_8px_24px_rgba(148,163,184,.25)]",
    bg:     "bg-gradient-to-br from-slate-50 to-slate-100",
    border: "border-slate-300",
    badge:  "from-slate-400 to-slate-500",
    text:   "from-slate-600 to-slate-700",
    emoji:  "🥈",
    scale:  "",
    size:   52,
  },
  3: {
    glow:   "ring-1 ring-orange-300 shadow-[0_8px_24px_rgba(251,146,60,.25)]",
    bg:     "bg-gradient-to-br from-orange-50 to-amber-50",
    border: "border-orange-300",
    badge:  "from-orange-400 to-orange-600",
    text:   "from-orange-600 to-amber-700",
    emoji:  "🥉",
    scale:  "",
    size:   52,
  },
};

function PodiumCard({ entry, rank }: { entry?: LeaderboardEntry; rank: number }) {
  if (!entry) return <div />;

  const cfg = PODIUM_CONFIG[rank] ?? PODIUM_CONFIG[3];
  const name = entry.full_name || entry.username || "Ẩn danh";

  return (
    <div className={`relative rounded-2xl ${cfg.bg} ${cfg.glow} ${cfg.scale} border ${cfg.border} p-3 sm:p-4 text-center transition-transform`}>
      {rank === 1 && (
        <Crown className="absolute -top-4 left-1/2 -translate-x-1/2 w-7 h-7 text-yellow-500 drop-shadow-md" fill="currentColor" />
      )}
      <div className="text-[20px] sm:text-[24px] mb-2">{cfg.emoji}</div>

      <div className="flex justify-center mb-2">
        <div className={`rounded-full p-0.5 bg-gradient-to-br ${cfg.badge}`}>
          <Avatar url={entry.avatar_url} name={name} size={cfg.size} />
        </div>
      </div>

      <p className="text-[11px] sm:text-[12px] font-bold text-[#0f172a] truncate">{name}</p>

      <p className={`text-[18px] sm:text-[22px] font-extrabold bg-gradient-to-r ${cfg.text} bg-clip-text text-transparent mt-1`}>
        {entry.total_xp.toLocaleString("vi")}
      </p>
      <p className="text-[9px] text-[#64748b] font-semibold -mt-0.5">XP</p>

      <div className="flex items-center justify-center gap-2 mt-2 text-[9px] sm:text-[10px] text-[#64748b] font-medium">
        <span className="inline-flex items-center gap-0.5"><BookOpen className="w-3 h-3" /> {entry.path_count}</span>
        <span className="inline-flex items-center gap-0.5"><Target className="w-3 h-3" /> {entry.accuracy}%</span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */

function LeaderboardRow({ entry, isMe, compact }: { entry: LeaderboardEntry; isMe?: boolean; compact?: boolean }) {
  const name = entry.full_name || entry.username || "Ẩn danh";

  return (
    <div
      className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${
        isMe ? "bg-teal-50/40" : "hover:bg-[#fafbfc]"
      }`}
    >
      <div className="w-8 h-8 rounded-full bg-[#f1f5f9] flex items-center justify-center text-[11px] font-extrabold text-[#64748b] shrink-0">
        #{entry.rank}
      </div>

      <div className="flex items-center gap-2.5 flex-1 min-w-0">
        <Avatar url={entry.avatar_url} name={name} size={36} />
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-bold text-[#0f172a] truncate flex items-center gap-1.5">
            {name}
            {isMe && <span className="text-[9px] px-1.5 py-[1px] rounded bg-teal-600 text-white font-bold">BẠN</span>}
          </p>
          {!compact && (
            <div className="flex items-center gap-2 mt-0.5 text-[10px] text-[#94a3b8] font-medium">
              <span className="inline-flex items-center gap-0.5"><Target className="w-2.5 h-2.5" /> {entry.correct_count} ({entry.accuracy}%)</span>
              <span className="inline-flex items-center gap-0.5"><BookOpen className="w-2.5 h-2.5" /> {entry.path_count}</span>
              <span className="inline-flex items-center gap-0.5"><FileText className="w-2.5 h-2.5" /> {entry.exam_count}</span>
              <span className="hidden sm:inline-flex items-center gap-0.5"><Brain className="w-2.5 h-2.5" /> {entry.mindmap_count}</span>
              <span className="hidden sm:inline-flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" /> {fmtTime(entry.time_spent)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="text-right shrink-0">
        <p className="text-[15px] font-extrabold text-teal-700">
          {entry.total_xp.toLocaleString("vi")}
        </p>
        <p className="text-[9px] text-[#94a3b8] font-bold -mt-0.5">XP</p>
      </div>
    </div>
  );
}